import React, { useState, useEffect } from 'react';
import { 
  Wand2, Scissors, LayoutDashboard, Zap, FileText, CheckCircle, 
  ArrowRight, Star, ShieldCheck, Shirt, MoveRight, Image as ImageIcon,
  Sparkles, Search, Tag, Play, Video
} from 'lucide-react';
import { CREDIT_PACKAGES } from '../constants';
import { UserProfile } from '../types';

interface LandingPageProps {
  onLogin: () => void;
  user: UserProfile | null;
  isLoading?: boolean;
}

const DASHBOARD_URL = 'https://dashboard.listingstudio.co';

const FeatureSpotlight = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // UPDATED: Using _v2 filenames to resolve caching/casing conflicts
  const features = [
    {
      id: 'listing',
      title: 'Listing Optimizer',
      desc: 'Generate SEO-optimized titles, descriptions, tags, and create promotional videos automatically.',
      image: '/listing_optimizer_v2.png'
    },
    {
      id: 'mockup',
      title: 'Smart Mockup',
      desc: 'Place designs on realistic T-shirts, mugs, and more. Uses smart warping to follow fabric folds.',
      image: '/mock_up_v2.png'
    },
    {
      id: 'enhancer',
      title: 'Photo Enhancer',
      desc: 'Turn raw snapshots into professional studio photography with AI lighting and background generation.',
      image: '/photo_enhancer_v2.png'
    }
  ];

  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      setActiveTab((prev) => (prev + 1) % features.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isPaused]);

  return (
    <div className="bg-white rounded-2xl shadow-2xl shadow-slate-200/50 border border-slate-100 overflow-hidden flex flex-col md:flex-row max-w-6xl mx-auto"
         onMouseEnter={() => setIsPaused(true)}
         onMouseLeave={() => setIsPaused(false)}>
      
      {/* Left Navigation */}
      <div className="md:w-1/3 bg-slate-50 border-r border-slate-100 flex flex-col">
        <div className="p-6 border-b border-slate-100">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">How Listing Studio Works</h4>
            <p className="text-sm text-slate-500">See how our AI tools accelerate your workflow.</p>
        </div>
        <div className="flex-1 flex flex-col justify-center">
            {features.map((feature, index) => (
            <button
                key={feature.id}
                onClick={() => setActiveTab(index)}
                className={`p-6 text-left transition-all duration-300 border-l-4 relative
                ${activeTab === index 
                    ? 'bg-white border-primary-500 shadow-sm z-10' 
                    : 'bg-transparent border-transparent hover:bg-slate-100 hover:border-slate-200 text-slate-500'}
                `}
            >
                <h3 className={`font-bold text-lg mb-2 ${activeTab === index ? 'text-slate-900' : 'text-slate-600'}`}>
                {feature.title}
                </h3>
                <p className={`text-sm leading-relaxed ${activeTab === index ? 'text-slate-600' : 'text-slate-400'}`}>
                {feature.desc}
                </p>
            </button>
            ))}
        </div>
      </div>

      {/* Right Image Display */}
      <div className="md:w-2/3 bg-slate-100/50 relative h-[400px] md:h-[600px] overflow-hidden flex items-center justify-center p-8">
         {/* Background decoration */}
         <div className="absolute inset-0 bg-[radial-gradient(#e2e8f0_1px,transparent_1px)] [background-size:20px_20px] opacity-50"></div>
         
         {features.map((feature, index) => (
           <div 
             key={feature.id}
             className={`absolute inset-0 flex items-center justify-center transition-all duration-700 ease-in-out px-8 py-12 md:px-16 md:py-20
               ${activeTab === index ? 'opacity-100 translate-x-0 scale-100' : 'opacity-0 translate-x-8 scale-95 pointer-events-none'}
             `}
           >
             {/* Floating Window Container with macOS Header */}
             <div className="relative shadow-2xl shadow-slate-400/50 rounded-xl overflow-hidden border border-slate-300 bg-white flex flex-col w-auto max-w-full" style={{ maxHeight: '100%' }}>
                 
                 {/* Window Header - Made background darker (slate-200) so it's clearly visible */}
                 <div className="h-10 bg-slate-200 border-b border-slate-300 flex items-center px-4 gap-2 shrink-0 z-20 w-full">
                    <div className="w-3 h-3 rounded-full bg-[#FF5F56] border border-[#E0443E] shadow-sm"></div>
                    <div className="w-3 h-3 rounded-full bg-[#FFBD2E] border border-[#DEA123] shadow-sm"></div>
                    <div className="w-3 h-3 rounded-full bg-[#27C93F] border border-[#1AAB29] shadow-sm"></div>
                 </div>

                 {/* Window Content */}
                 <div className="flex-1 bg-white relative overflow-hidden flex items-center justify-center min-h-0">
                     <img 
                        src={feature.image} 
                        alt={feature.title} 
                        className="block w-auto h-auto max-w-full max-h-full object-contain"
                        style={{ maxHeight: 'calc(100% - 2px)' }} 
                     />
                 </div>
             </div>
           </div>
         ))}
      </div>
    </div>
  );
};

export const LandingPage: React.FC<LandingPageProps> = ({ onLogin, user, isLoading }) => {
  return (
    <div className="min-h-screen bg-white font-sans text-slate-900">
      {/* Navbar */}
      <nav className="fixed w-full bg-white/90 backdrop-blur-xl border-b border-slate-200/80 z-50 transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-20 items-center">
            <div className="flex items-center gap-3 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <div className="relative">
                 <div className="absolute inset-0 bg-primary-500 blur-md opacity-20 group-hover:opacity-40 transition-opacity rounded-full"></div>
                 <img 
                    src="/favicon.png" 
                    alt="Logo" 
                    className="w-10 h-10 relative z-10 transform group-hover:scale-105 transition-transform rounded-lg" 
                 />
              </div>
              <span className="font-bold text-xl tracking-tight text-slate-800 group-hover:text-primary-600 transition-colors">
                <span className="text-primary-600">Listing</span> <span className="text-slate-700">Studio</span>
              </span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm font-medium text-slate-600 hover:text-primary-600 transition-colors">Features</a>
              <a href="#pricing" className="text-sm font-medium text-slate-600 hover:text-primary-600 transition-colors">Pricing</a>
              <a href="#faq" className="text-sm font-medium text-slate-600 hover:text-primary-600 transition-colors">FAQ</a>
              
              {isLoading ? (
                  <div className="w-32 h-10 bg-slate-100 rounded-full animate-pulse"></div>
              ) : user ? (
                   <a 
                     href={DASHBOARD_URL}
                     className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg shadow-primary-900/20 hover:shadow-primary-900/30 hover:-translate-y-0.5 active:translate-y-0 flex items-center gap-2"
                   >
                     Go to Dashboard <ArrowRight size={16} />
                   </a>
              ) : (
                   <button 
                     onClick={onLogin}
                     className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2.5 rounded-full text-sm font-semibold transition-all shadow-lg shadow-slate-900/20 hover:shadow-slate-900/30 hover:-translate-y-0.5 active:translate-y-0"
                   >
                     Get Started
                   </button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-indigo-50 via-white to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center max-w-4xl mx-auto mb-20">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white text-slate-600 text-sm font-medium mb-8 border border-slate-200 shadow-sm hover:shadow-md transition-shadow cursor-default animate-fade-in-up">
              <Star size={14} className="fill-amber-400 text-amber-400" />
              <span>Trusted by 100+ E-commerce Sellers</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight text-slate-900 mb-8 leading-[1.1] animate-fade-in-up" style={{animationDelay: '0.1s'}}>
              Design, Mockup, & List <br className="hidden md:block" />
              <span className="bg-gradient-to-r from-primary-600 via-indigo-600 to-primary-600 bg-clip-text text-transparent bg-[length:200%_auto] animate-[gradient_4s_linear_infinite]">
                10x Faster with AI
              </span>
            </h1>
            <p className="text-xl text-slate-600 mb-10 leading-relaxed max-w-2xl mx-auto animate-fade-in-up" style={{animationDelay: '0.2s'}}>
              The all-in-one creative suite for E-commerce & POD. Enhance product photos, extract designs, generate realistic mockups, 
              optimize SEO listings, and create videos in seconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up" style={{animationDelay: '0.3s'}}>
              {isLoading ? (
                  <div className="w-56 h-14 bg-slate-100 rounded-2xl animate-pulse"></div>
              ) : user ? (
                  <a 
                    href={DASHBOARD_URL}
                    className="group bg-primary-600 hover:bg-primary-700 text-white text-lg px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-primary-600/30 hover:shadow-primary-600/50 hover:-translate-y-1 flex items-center justify-center gap-2"
                  >
                    Go to Dashboard
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </a>
              ) : (
                  <button 
                    onClick={onLogin}
                    className="group bg-primary-600 hover:bg-primary-700 text-white text-lg px-8 py-4 rounded-2xl font-bold transition-all shadow-xl shadow-primary-600/30 hover:shadow-primary-600/50 hover:-translate-y-1 flex items-center justify-center gap-2"
                  >
                    Start Creating for Free 
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                  </button>
              )}
              
              <a 
                href="#features"
                className="bg-white border border-slate-200 hover:border-slate-300 text-slate-700 hover:bg-slate-50 text-lg px-8 py-4 rounded-2xl font-semibold transition-all shadow-sm hover:shadow-md"
              >
                View Features
              </a>
            </div>
            <p className="mt-6 text-sm text-slate-500 flex items-center justify-center gap-6 animate-fade-in-up" style={{animationDelay: '0.4s'}}>
               <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-green-500" /> No credit card required</span>
               <span className="flex items-center gap-1.5"><CheckCircle size={14} className="text-green-500" /> 50 Free Credits</span>
            </p>
          </div>

          {/* App Screenshot Mockup */}
          <div className="relative mx-auto max-w-6xl animate-fade-in-up" style={{animationDelay: '0.5s'}}>
             <div className="absolute -inset-4 bg-gradient-to-r from-primary-500 to-indigo-500 rounded-[2rem] blur-2xl opacity-10"></div>
             <FeatureSpotlight />
          </div>
        </div>
      </div>

      {/* Features Grid */}
      <div id="features" className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Everything You Need to Scale</h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Replace your entire design team, photographer, and SEO expert with one powerful dashboard.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <FeatureCard 
              icon={<Scissors className="text-pink-500" size={24} />}
              title="Smart Design Extraction"
              description="Upload any clothing photo. AI instantly removes the cloth, wrinkles, and background to give you a clean, transparent PNG design file."
            />
            <FeatureCard 
              icon={<Wand2 className="text-purple-500" size={24} />}
              title="AI Redesign & Masking"
              description="Want to change just the cat in the design? Use the magic brush to mask an area and tell AI what to change it into."
            />
            <FeatureCard 
              icon={<LayoutDashboard className="text-blue-500" size={24} />}
              title="Instant Mockups"
              description="Place your design on realistic T-shirts, Hoodies, and Mugs. Generates professional product photography in seconds."
            />
            <FeatureCard 
              icon={<FileText className="text-green-500" size={24} />}
              title="Listing Optimizer"
              description="Analyze your listing. Get SEO-optimized titles, tags, and risk analysis for IP infringement automatically."
            />
            <FeatureCard 
              icon={<Zap className="text-amber-500" size={24} />}
              title="Video Generation"
              description="Turn static product images into cinematic 8-second promotional videos for TikTok and Reels using Google Veo."
            />
            <FeatureCard 
              icon={<Sparkles className="text-indigo-500" size={24} />}
              title="Product Enhancer"
              description="Upload raw photos of resin lamps, shoes, or crafts. AI transforms them into high-end studio photography."
            />
          </div>
        </div>
      </div>

      {/* Pricing Section */}
      <div id="pricing" className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Simple Pay-As-You-Go Pricing</h2>
            <p className="text-lg text-slate-600">No monthly subscriptions. Credits never expire. Purchase only when you need.</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {CREDIT_PACKAGES.map((pkg) => (
              <div key={pkg.id} className={`relative p-8 rounded-3xl transition-transform hover:-translate-y-1 duration-300 ${pkg.isPopular ? 'border-2 border-primary-500 shadow-2xl shadow-primary-900/10 bg-white z-10 transform md:-translate-y-4' : 'border border-slate-200 bg-white shadow-xl shadow-slate-200/50'}`}>
                {pkg.isPopular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-600 text-white px-4 py-1 rounded-full text-sm font-bold tracking-wide shadow-lg shadow-primary-600/30">
                    MOST POPULAR
                  </div>
                )}
                <h3 className="text-xl font-bold text-slate-900 mb-2">{pkg.name}</h3>
                <div className="flex items-baseline gap-1 mb-6">
                  <span className="text-5xl font-extrabold text-slate-900">${pkg.price}</span>
                  <span className="text-slate-500 font-medium">/ one-time</span>
                </div>
                <div className="mb-8">
                  <div className="inline-block rounded-lg bg-primary-50 px-4 py-2 border border-primary-100">
                    <span className="text-lg font-bold text-primary-700">
                      {pkg.credits} Credits
                    </span>
                  </div>
                </div>
                <ul className="space-y-4 mb-8 border-t border-slate-100 pt-8">
                  {pkg.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-600">
                      <div className="mt-0.5 rounded-full bg-green-100 p-0.5 text-green-600">
                         <CheckCircle size={14} />
                      </div>
                      <span className="text-sm font-medium">{feat}</span>
                    </li>
                  ))}
                </ul>
                
                {isLoading ? (
                    <div className="w-full h-14 bg-slate-100 rounded-xl animate-pulse"></div>
                ) : user ? (
                   <a 
                     href={DASHBOARD_URL}
                     className={`block w-full text-center py-4 rounded-xl font-bold transition-all ${
                        pkg.isPopular 
                          ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-600/30' 
                          : 'bg-slate-900 text-white hover:bg-slate-800 shadow-lg'
                      }`}
                   >
                     Buy {pkg.name}
                   </a>
                ) : (
                    <button 
                      onClick={onLogin}
                      className={`w-full py-4 rounded-xl font-bold transition-all ${
                        pkg.isPopular 
                          ? 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg shadow-primary-600/30' 
                          : 'bg-slate-900 text-white hover:bg-slate-800 shadow-lg'
                      }`}
                    >
                      Get Started
                    </button>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div id="faq" className="py-32 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <FaqItem 
              q="How do credits work?" 
              a="Each action costs a certain number of credits (e.g., Removing Background is 1 credit, Redesigning is 8). You only pay for what you use, and credits remain in your account until used." 
            />
            <FaqItem 
              q="Can I use the images commercially?" 
              a="Yes! All images generated and processed by Product Studio AI are 100% yours to use for commercial listings on Etsy, Shopify, Amazon, and more." 
            />
            <FaqItem 
              q="Does it work with Midjourney images?" 
              a="Absolutely. You can upload images from Midjourney, DALL-E, Stable Diffusion, or your own photos. Our AI is specifically trained to handle AI-generated art for commercial purposes." 
            />
            <FaqItem 
              q="What happens if an AI generation fails?" 
              a="If the AI fails to generate a usable result due to a system error, your credits are automatically refunded for that specific action." 
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-slate-50 border-t border-slate-200 pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid md:grid-cols-4 gap-12 mb-16">
              <div className="col-span-2">
                 <div className="flex items-center gap-2 mb-6">
                    <img 
                        src="/favicon.png" 
                        alt="Logo" 
                        className="w-8 h-8 rounded-md" 
                    />
                    <span className="font-bold text-xl text-slate-900">Listing Studio</span>
                 </div>
                 <p className="text-slate-500 max-w-xs leading-relaxed">
                    Empowering E-commerce sellers with cutting-edge AI tools to design better and sell more.
                 </p>
              </div>
              <div>
                 <h4 className="font-bold text-slate-900 mb-6">Product</h4>
                 <ul className="space-y-4 text-slate-500">
                    <li><a href="#features" className="hover:text-primary-600">Features</a></li>
                    <li><a href="#pricing" className="hover:text-primary-600">Pricing</a></li>
                    <li><a href="#" className="hover:text-primary-600">Login</a></li>
                 </ul>
              </div>
              <div>
                 <h4 className="font-bold text-slate-900 mb-6">Legal</h4>
                 <ul className="space-y-4 text-slate-500">
                    <li><a href="#" className="hover:text-primary-600">Privacy Policy</a></li>
                    <li><a href="#" className="hover:text-primary-600">Terms of Service</a></li>
                    <li><a href="#" className="hover:text-primary-600">Contact Support</a></li>
                 </ul>
              </div>
           </div>
           <div className="pt-8 border-t border-slate-200 flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-slate-400 text-sm">© {new Date().getFullYear()} Listing Studio. All rights reserved.</p>
              <div className="flex gap-6">
                 {/* Social placeholders */}
                 <div className="w-5 h-5 bg-slate-300 rounded-full"></div>
                 <div className="w-5 h-5 bg-slate-300 rounded-full"></div>
                 <div className="w-5 h-5 bg-slate-300 rounded-full"></div>
              </div>
           </div>
        </div>
      </footer>
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: any, title: string, description: string }) => (
  <div className="bg-white p-8 rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform group-hover:bg-primary-50">
      {icon}
    </div>
    <h3 className="text-xl font-bold text-slate-900 mb-3">{title}</h3>
    <p className="text-slate-600 leading-relaxed">{description}</p>
  </div>
);

const FaqItem = ({ q, a }: { q: string, a: string }) => (
  <div className="bg-slate-50 p-8 rounded-3xl hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-slate-200">
    <h4 className="text-lg font-bold text-slate-900 mb-3">{q}</h4>
    <p className="text-slate-600 leading-relaxed">{a}</p>
  </div>
);